// Delivery reports: a project's progress (on its Overview) and the portfolio (Reports → Delivery).
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { get } from '../api';
import { ago } from '../ui';
import type { Project } from '../types';
import { rand } from './Pipeline';

interface Point { day: string; ideal: number; open?: number; scope?: number; hours?: number }
interface Sprint { id: string; name: string; starts_on: string; ends_on: string; finished: boolean; total: number; done: number; days_left: number; elapsed: number; behind: boolean; points: Point[] }
interface ProgressData {
  open: number; done: number; overdue: number; by_status: { name: string; category: string; count: number }[]; by_priority: Record<string, number>;
  by_assignee: { name: string; count: number }[]; throughput: { week: string; created: number; completed: number }[];
  cycle_days: { median: number; count: number } | null; sprint: Sprint | null; sprints: { id: string; name: string; finished: boolean }[];
  internal?: { hours_open: number; hours_done: number; unestimated: number; cost_cents: number };
}

const short = (d: string) => new Date(d + 'T00:00').toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });

/** Burndown: ideal (dashed), open tickets (solid), scope (faint). */
function Burndown({ s }: { s: Sprint }) {
  const W = 340, H = 150, pad = { l: 24, r: 6, t: 8, b: 20 };
  const max = Math.max(1, ...s.points.map((p) => Math.max(p.ideal, p.open ?? 0, p.scope ?? 0)));
  const x = (i: number) => pad.l + (i * (W - pad.l - pad.r)) / Math.max(1, s.points.length - 1);
  const y = (v: number) => pad.t + (1 - v / max) * (H - pad.t - pad.b);
  const line = (key: 'ideal' | 'open' | 'scope') => s.points.map((p, i) => (p[key] == null ? null : `${x(i)},${y(p[key]!)}`)).filter(Boolean).join(' ');
  const todayIdx = s.points.findIndex((p) => p.open == null) - 1;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="burndown" role="img" aria-label={`Burndown for ${s.name}`}>
      {[0, 0.5, 1].map((f) => <g key={f}><line x1={pad.l} x2={W - pad.r} y1={y(max * f)} y2={y(max * f)} className="grid" /><text x={pad.l - 6} y={y(max * f) + 4} className="axis" textAnchor="end">{Math.round(max * f)}</text></g>)}
      <polyline points={line('scope')} className="scope" />
      <polyline points={line('ideal')} className="ideal" />
      <polyline points={line('open')} className="open" />
      {todayIdx >= 0 && !s.finished && <line x1={x(todayIdx)} x2={x(todayIdx)} y1={pad.t} y2={H - pad.b} className="today" />}
      <text x={pad.l} y={H - 6} className="axis">{short(s.starts_on)}</text>
      <text x={W - pad.r} y={H - 6} className="axis" textAnchor="end">{short(s.ends_on)}</text>
    </svg>
  );
}

export function ProjectProgress({ project }: { project: Project }) {
  const [section, setSection] = useState('');
  const { data } = useQuery({ queryKey: ['progress', project.id, section], queryFn: () => get<ProgressData>(`/api/projects/${project.id}/progress${section ? `?section=${section}` : ''}`) });
  if (!data || data.open + data.done === 0) return null;
  const s = data.sprint;
  const tMax = Math.max(1, ...data.throughput.map((w) => Math.max(w.created, w.completed)));
  const statusMax = Math.max(1, ...data.by_status.map((x) => x.count));
  return (
    <div className="grid-3 progress">
      {s && (
        <section className="card pad stack" style={{ gap: 8 }}>
          <div className="row" style={{ gap: 8 }}>
            <h2 className="grow">{s.name}</h2>
            {data.sprints.length > 1 && <select className="input compact" value={section || s.id} onChange={(e) => setSection(e.target.value)} aria-label="Sprint">
              {data.sprints.map((x) => <option key={x.id} value={x.id}>{x.name}{x.finished ? ' (finished)' : ''}</option>)}</select>}
          </div>
          <div className="small"><b>{s.done} of {s.total}</b> done · {s.finished ? 'finished' : `${s.days_left} day${s.days_left === 1 ? '' : 's'} left`}
            {s.behind && <span className="bad-text"> · behind</span>}</div>
          <Burndown s={s} />
          <div className="row tiny muted" style={{ gap: 12 }}><span><span className="key bd-open" />Open</span><span><span className="key bd-ideal" />Ideal</span><span><span className="key bd-scope" />Scope</span></div>
        </section>
      )}
      <section className="card pad stack" style={{ gap: 8 }}>
        <h2>Created and done</h2>
        <div className="muted tiny">Tickets per week, last 12 weeks{data.cycle_days ? ` · typically ${data.cycle_days.median} days from raised to done` : ''}</div>
        <div className="vbars short" role="img" aria-label="Tickets created and done per week">
          {data.throughput.map((w) => (
            <div key={w.week} className="vbar-col" title={`Week of ${short(w.week)}: ${w.created} created, ${w.completed} done`}>
              <div className="vbar-pair"><span className="vbar created" style={{ height: `${(100 * w.created) / tMax}%` }} /><span className="vbar won" style={{ height: `${(100 * w.completed) / tMax}%` }} /></div>
            </div>
          ))}
        </div>
        <div className="row tiny muted" style={{ gap: 12 }}><span><span className="key created" />Created</span><span><span className="key won" />Done</span></div>
      </section>
      <section className="card pad stack" style={{ gap: 8 }}>
        <h2>Open work</h2>
        <div className="small"><b>{data.open}</b> open{data.overdue > 0 && <> · <span className="bad-text">{data.overdue} overdue</span></>}</div>
        {data.by_status.map((x) => (
          <div key={x.name} className="hbar"><span className="hbar-label small">{x.name}</span>
            <span className="hbar-track"><span className={`hbar-part ${x.category === 'done' ? 'done' : ''}`} style={{ width: `${(100 * x.count) / statusMax}%` }} /></span><span className="hbar-value small">{x.count}</span></div>
        ))}
        {data.by_assignee.length > 0 && <div className="tiny muted">{data.by_assignee.map((a) => `${a.name} ${a.count}`).join(' · ')}</div>}
        {data.internal && <div className="tiny muted internal-line">Blue Market only: {data.internal.hours_open} h open, {data.internal.hours_done} h done
          {data.internal.unestimated > 0 && `, ${data.internal.unestimated} unestimated`}{data.internal.cost_cents > 0 && ` · cost ${rand(data.internal.cost_cents)}`}</div>}
      </section>
    </div>
  );
}

// ---------- the portfolio ----------

interface PortfolioRow { id: string; name: string; client: { id: string; name: string }; state: string;
  tickets: { open: number; doing: number; overdue: number; done_30: number; created_30: number };
  sprint: { name: string; ends_on: string; total: number; done: number; elapsed: number; behind: boolean } | null;
  round: { number: number; visibility: string } | null; questions_waiting: { count: number; since: string } | null; last_activity: string; flags: string[] }
const FLAG: Record<string, string> = { overdue: 'Overdue tickets', behind: 'Sprint behind', waiting: 'Waiting on client', quiet: 'Quiet for 2 weeks' };

export function Portfolio() {
  const { data } = useQuery({ queryKey: ['portfolio'], queryFn: () => get<{ projects: PortfolioRow[]; totals: Record<string, number> }>('/api/reports/delivery') });
  const [attention, setAttention] = useState(false);
  if (!data) return <span className="spin muted" />;
  const rows = data.projects.filter((p) => !attention || p.flags.length);
  const t = data.totals;
  return (
    <>
      <div className="tiles">
        <div className="tile static"><span className="tile-label">Active projects</span><span className="tile-value">{data.projects.length}</span><span className="tile-sub">{t.attention ?? 0} need attention</span></div>
        <div className="tile static"><span className="tile-label">Open tickets</span><span className="tile-value">{t.open ?? 0}</span><span className="tile-sub">{t.overdue ? <span className="bad-text">{t.overdue} overdue</span> : 'none overdue'}</span></div>
        <div className="tile static"><span className="tile-label">Done, last 30 days</span><span className="tile-value">{t.done_30 ?? 0}</span><span className="tile-sub">{t.created_30 ?? 0} raised</span></div>
      </div>
      <section className="card" style={{ marginTop: 16 }}>
        <div className="card-head"><h2>Projects</h2><span className="grow" />
          <label className="row small" style={{ gap: 6 }}><input type="checkbox" checked={attention} onChange={(e) => setAttention(e.target.checked)} />Only those needing attention</label></div>
        <div className="table-wrap">
          <table className="table portfolio">
            <thead><tr><th>Project</th><th className="num">Open</th><th className="num hide-sm">Done 30d</th><th className="hide-sm">Sprint</th><th className="hide-sm">Designs</th><th className="hide-sm">Last activity</th><th /></tr></thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id}>
                  <td><Link to={`/projects/${p.id}`}><b>{p.name}</b></Link><div className="muted tiny">{p.client.name} · {p.state}</div></td>
                  <td className="num">{p.tickets.open}{p.tickets.overdue > 0 && <div className="bad-text tiny">{p.tickets.overdue} overdue</div>}</td>
                  <td className="num hide-sm">{p.tickets.done_30 || '–'}</td>
                  <td className="hide-sm">{p.sprint ? <div style={{ minWidth: 120 }}>
                    <div className="tiny">{p.sprint.name}: {p.sprint.done}/{p.sprint.total}</div>
                    <div className="mini-progress" title={`${p.sprint.elapsed}% of the time gone`}><span style={{ width: `${p.sprint.total ? (100 * p.sprint.done) / p.sprint.total : 0}%` }} /><i style={{ left: `${p.sprint.elapsed}%` }} /></div>
                  </div> : <span className="muted tiny">–</span>}</td>
                  <td className="hide-sm">{p.round ? <span className="tiny">v{p.round.number} · {p.round.visibility}</span> : <span className="muted tiny">–</span>}
                    {p.questions_waiting && <div className="tiny muted">{p.questions_waiting.count} question{p.questions_waiting.count === 1 ? '' : 's'} with the client</div>}</td>
                  <td className="hide-sm tiny muted">{ago(p.last_activity)}</td>
                  <td><div className="row wrap" style={{ gap: 4, justifyContent: 'flex-end' }}>{p.flags.map((f) => <span key={f} className={`badge tiny-badge ${f === 'quiet' ? '' : 'warn'}`}>{FLAG[f]}</span>)}</div></td>
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length && <div className="empty small">{attention ? 'Nothing needs attention.' : 'No active projects.'}</div>}
        </div>
      </section>
    </>
  );
}
